import { FaceCullingMode, RenderLayer, } from '@skand/three-js-renderer';
import { Color, Vector2, Vector3 } from 'three';
import { PointIcon } from '../../Assets';
import { calculateCentroid, lengthToString, triangulate } from '../../Utils';
/**
 * Color of a highlighted vertex.
 */
const HIGHLIGHT_COLOR = new Color(1, 1, 0);
/**
 * 3D annotation editor controller.
 */
export class Editor {
    constructor(renderer, labelManager) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labelManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "vertices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "closed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "color", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "measurementUnit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "vertexCursor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lineMesh", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lineObject", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "polygonMesh", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "polygonObject", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.renderer = renderer;
        this.labelManager = labelManager;
        this.vertices = [];
        this.closed = false; // Polygon or polyline?
        this.color = new Color(1, 1, 1);
        this.measurementUnit = 'metric';
        this.vertexCursor = null;
        this.lineMesh = this.renderer.createLineMesh();
        this.lineObject = this.renderer.getObjectFactory().createRawMeshObject({
            mesh: this.lineMesh,
            position: {
                ecef: { x: 0, y: 0, z: 0 },
            },
            material: {
                type: 'basicOpaque',
                color: this.color,
            },
        });
        this.lineObject.setRenderLayer(RenderLayer.AfterPanorama);
        this.polygonMesh = this.renderer.createIndexedTriangleMesh();
        this.polygonObject = this.renderer.getObjectFactory().createRawMeshObject({
            mesh: this.polygonMesh,
            position: {
                ecef: { x: 0, y: 0, z: 0 },
            },
            material: {
                type: 'basicAlpha',
                color: this.color,
                alpha: 0.5,
                faceCulling: FaceCullingMode.None,
            },
        });
        this.polygonObject.setRenderLayer(RenderLayer.AfterPanorama);
        this.polygonObject.setVisible(false);
        this.renderer.getWorld().addObject(this.lineObject);
        this.renderer.getWorld().addObject(this.polygonObject);
        this.labels = [];
    }
    /**
     * Update the colors of the vertices and geometry.
     */
    updateColors() {
        for (const vertex of this.vertices) {
            vertex.applyMaterial({
                type: 'basicOpaque',
                color: this.color,
            });
        }
        this.lineObject.applyMaterial({
            type: 'basicOpaque',
            color: this.color,
        });
        this.polygonObject.applyMaterial({
            type: 'basicAlpha',
            color: this.color,
            alpha: 0.5,
            faceCulling: FaceCullingMode.None,
        });
    }
    /**
     * Update the measurement and point name labels.
     *
     * @param withCursor Whether to include the cursor in the mesh
     */
    updateLabels() {
        // Clear existing labels
        while (this.labels.length > 0) {
            this.labelManager.remove(this.labels[this.labels.length - 1]);
            this.labels.pop();
        }
        // Add edge labels
        const vertices = this.vertices.map(vertex => {
            const position = new Vector3();
            vertex.getPositionECEF(position);
            return position;
        });
        if (this.closed) {
            vertices.push(vertices[0].clone());
        }
        else if (this.vertexCursor !== null) {
            vertices.push(this.vertexCursor.clone());
        }
        for (let i = 0; i < vertices.length - 1; i++) {
            const a = vertices[i];
            const b = vertices[i + 1];
            const distance = a.distanceTo(b);
            const string = lengthToString(distance, this.measurementUnit, 2);
            const midpoint = a.clone().add(b).multiplyScalar(0.5);
            this.labels.push(this.labelManager.add(string, midpoint, true));
        }
        // Add point labels
        const uniqueVertices = this.vertices.map(vertex => {
            const position = new Vector3();
            vertex.getPositionECEF(position);
            return position;
        });
        for (let i = 0; i < uniqueVertices.length; i++) {
            const vertex = uniqueVertices[i];
            this.labels.push(this.labelManager.add(`P${i + 1}`, vertex, true, new Vector2(0, -20)));
        }
    }
    /**
     * Update the meshes for the polyline and polygon.
     */
    updateMeshes() {
        const vertices = this.vertices.map(vertex => {
            const position = new Vector3();
            vertex.getPositionECEF(position);
            return position;
        });
        if (this.closed) {
            vertices.push(vertices[0].clone());
        }
        else if (this.vertexCursor !== null) {
            vertices.push(this.vertexCursor.clone());
        }
        // Recenter
        const centroid = calculateCentroid(vertices);
        for (const vertex of vertices) {
            vertex.sub(centroid);
        }
        this.lineMesh.vertices = [];
        for (let i = 0; i < vertices.length - 1; i++) {
            this.lineMesh.vertices.push(vertices[i], vertices[i + 1]);
        }
        this.lineMesh.resolve();
        // Update geometry
        this.polygonMesh.vertices = vertices.slice();
        this.polygonMesh.indices = triangulate(vertices);
        this.polygonMesh.resolve();
        this.lineObject.setPositionECEF(centroid);
        this.polygonObject.setPositionECEF(centroid);
        this.updateLabels();
    }
    /**
     * Add a new vertex to the current sketch.
     *
     * @param position
     */
    async addVertex(position) {
        const sprite = await this.renderer.getObjectFactory().createSprite({
            position: {
                ecef: position,
            },
            url: PointIcon,
            material: {
                type: 'basicOpaque',
                color: this.color,
            },
            sizeAttenuation: false,
            scale: { x: 0.03, y: 0.03, z: 0.03 },
        });
        sprite.setRenderLayer(RenderLayer.AfterPanorama);
        sprite.setVisible(true);
        this.renderer.getWorld().addObject(sprite);
        this.vertices.push(sprite);
        this.highlightVertex(sprite);
        this.updateMeshes();
    }
    /**
     * Remove a vertex from the current sketch.
     *
     * @param vertex
     * @returns
     */
    removeVertex(vertex) {
        const index = this.vertices.findIndex(query => query === vertex);
        if (index < 0)
            return;
        if (this.closed) {
            // Make the preceding vertex the tail of the new polyline
            let prevIndex = index - 1;
            if (prevIndex < 0)
                prevIndex = this.vertices.length - 1;
            for (let i = 0; i < this.vertices.length - prevIndex - 1; i++) {
                const tail = this.vertices[this.vertices.length - 1];
                this.vertices.pop();
                this.vertices.unshift(tail);
            }
            this.openPolygon();
        }
        else {
            // Otherwise, just remove the vertex directly
            this.vertices.splice(index, 1);
            this.renderer.getWorld().removeObject(vertex);
        }
        this.highlightVertex(null);
        this.updateMeshes();
    }
    /**
     * Clear the sketch.
     */
    clear() {
        while (this.vertices.length > 0) {
            this.removeVertex(this.vertices[0]);
        }
        this.setCursor(null);
    }
    /**
     * Close the polyline to convert it into a polygon.
     */
    closePolygon() {
        if (this.vertices.length >= 3) {
            this.closed = true;
            this.polygonObject.setVisible(true);
            this.updateMeshes();
        }
    }
    /**
     * Open the polygon, converting into a polyline by disconnecting the start and end vertices.
     */
    openPolygon() {
        if (this.vertices.length >= 3) {
            this.closed = false;
            this.polygonObject.setVisible(false);
            this.updateMeshes();
        }
    }
    /**
     * Set the draw color.
     *
     * @param color
     */
    setColor(color) {
        this.color = color;
        this.updateColors();
    }
    /**
     * Set the units of the measurement labels.
     *
     * @param measurementUnit
     */
    setMeasurementUnit(measurementUnit) {
        this.measurementUnit = measurementUnit;
        this.updateLabels();
    }
    /**
     * Set the position of the cursor.
     *
     * @param position
     */
    setCursor(position) {
        this.vertexCursor = position;
        this.updateMeshes();
    }
    /**
     * Set the currently selected vertex.
     */
    highlightVertex(vertex) {
        for (const v of this.vertices) {
            const color = v === vertex ? HIGHLIGHT_COLOR : this.color;
            v.applyMaterial({
                type: 'basicOpaque',
                color,
            });
        }
    }
    /**
     * Get the list of vertices.
     *
     * @returns
     */
    getVertices() {
        return this.vertices;
    }
    /**
     * Check if the polygon is closed.
     *
     * @returns
     */
    isPolygonClosed() {
        return this.closed;
    }
    /**
     * Get the color of the polygon.
     *
     * @returns
     */
    getColor() {
        return this.color;
    }
    /**
     * Check if the polygon can be closed.
     *
     * @returns
     */
    canClosePolygon() {
        return !this.closed && this.vertices.length >= 3;
    }
}
